import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import type { User } from '../types';

interface AuthValue {
  user: User | null; loading: boolean; login: (email: string, password: string) => Promise<void>; logout: () => void;
  hasPermission: (permission: string) => boolean; refresh: () => Promise<void>;
}
const AuthContext = createContext<AuthValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    const token = localStorage.getItem('eagleeye_token');
    if (!token) { setUser(null); setLoading(false); return; }
    try { setUser((await api.get('/auth/me')).data.user); }
    catch { localStorage.removeItem('eagleeye_token'); setUser(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { void refresh(); }, []);
  const login = async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    localStorage.setItem('eagleeye_token', response.data.token);
    setUser(response.data.user);
  };
  const logout = () => { localStorage.removeItem('eagleeye_token'); setUser(null); };
  const permissions = useMemo(() => new Set(user?.permissions ?? []), [user]);
  return <AuthContext.Provider value={{ user, loading, login, logout, refresh, hasPermission: (permission) => permissions.has(permission) }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
};
